import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { formatMoeda, contar } from '@fiodecobre/shared';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useAlertaFaixa } from './alertas/AlertasContexto';

// Faixa de CONTA NEGATIVA ([DA-551], pedido do Ruan em 15/08/2026, logo depois do marco zero da
// [DA-550]).
//
// POR QUE EXISTE: conta negativa nao e informacao, e sintoma — significa que saiu dinheiro que
// nunca foi registrado entrando. Ate aqui isso so aparecia quando alguem ESBARRAVA na trava
// anti-negativo tentando pagar, ou seja no pior momento possivel, com o fornecedor esperando.
// Foi assim que a Cora chegou a -R$ 10.599,89 sem ninguem perceber: ela pagava fornecedor toda
// semana com dinheiro que chegava por transferencia nunca lancada.
//
// SO ADMIN, palavra dele. Nao herda o `['admin','gerente']` das faixas vizinhas de proposito: a
// resposta a uma conta negativa e lancar a entrada que faltou ou ir atras do extrato, e as duas
// coisas sao dele.
//
// MEDE PELA REGUA DA TRAVA (`GET /api/caixa/contas-negativas` chama o mesmo
// `saldoParaSaidaDaConta` que a trava usa), entao a faixa acende exatamente quando a saida seria
// recusada — nem antes, nem depois. Faixa que acende sem a trava barrar vira alarme falso, e
// alarme falso ensina a ignorar o vermelho ([DA-410]).
//
// SEM O "X" DE DISPENSAR, como a de estoque negativo e a de divergencia de caixa: as faixas
// dispensaveis avisam de algo que o TEMPO resolve (conta que vence). Aqui o numero so cai quando
// alguem CORRIGE — dispensar por um dia seria esconder o buraco em vez de fecha-lo.
//
// LARANJA, nao vermelho: e ATENCAO, nao erro (vermelho fica pro que quebrou; verde nunca, por
// daltonismo). Mesma cor que o aviso de conta negativa dentro dos modais de dinheiro, pra ser
// reconhecido como a mesma coisa.
//
// FREQUENCIA MEDIDA ANTES DE CONSTRUIR (15/08/2026): hoje zero contas negativas — a faixa nasce
// apagada. No historico eram 57 turnos fechados no negativo, quase todos de conta BANCARIA medida
// pela regua artificial que a [DA-550] aboliu; em agosto sobrou 1, o proprio caso consertado. O
// Movimento de Caixa (gaveta de verdade) fez 10 em junho, 8 em julho e ZERO em agosto, depois da
// trava. Ou seja: alarme raro, que e a unica condicao em que um alarme continua sendo lido.
interface ContaNegativa { id: number; nome: string; tipo: string; saldo: number }

const POLLING_MS = 5 * 60 * 1000;

export function AlertaContaNegativaBanner() {
  const navigate = useNavigate();
  const { usuario } = useAuth();
  const [contas, setContas] = useState<ContaNegativa[] | null>(null);

  const podeVer = usuario?.perfil === 'admin';

  useEffect(() => {
    if (!podeVer) return;
    let cancelado = false;
    async function tick() {
      try {
        const r = await api.get<{ contas: ContaNegativa[] }>('/api/caixa/contas-negativas');
        if (!cancelado) setContas(r.contas ?? []);
      } catch { /* silencioso: a faixa some, o resto da tela nao cai por causa de um aviso */ }
    }
    void tick();
    const id = setInterval(tick, POLLING_MS);
    return () => { cancelado = true; clearInterval(id); };
  }, [podeVer]);

  const ativas = podeVer && contas ? contas : [];
  const tem = ativas.length > 0;

  // Antes de qualquer early return — regra dos hooks.
  const recolhido = useAlertaFaixa(
    'conta-negativa',
    tem ? { rotulo: 'Conta negativa', bg: '#f97316', borda: '#c2410c', qtd: ativas.length } : null,
  );

  if (!tem || recolhido) return null;

  // UMA conta: diz o nome e o valor, que e o que resolve. VARIAS: diz quantas e mostra a pior —
  // listar cinco nomes numa faixa de 35px nao cabe, e o admin vai pra tela de qualquer jeito.
  const pior = ativas[0]!;
  const texto = ativas.length === 1
    ? `${pior.nome} esta em ${formatMoeda(pior.saldo)}`
    : `${contar(ativas.length, 'conta')} abaixo de zero — a pior e ${pior.nome}, em ${formatMoeda(pior.saldo)}`;

  return (
    <div
      style={{
        background: '#f97316',
        borderLeft: '4px solid #c2410c',
        color: '#000',
        padding: '8px 14px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        fontSize: 13,
        cursor: 'pointer',
      }}
      onClick={() => navigate('/financeiro')}
    >
      <AlertTriangle size={16} />
      <span style={{ fontWeight: 600 }}>Conta negativa:</span>
      <span>{texto} — saiu dinheiro que nunca foi lancado entrando</span>
      <span style={{ marginLeft: 'auto', textDecoration: 'underline' }}>Conferir</span>
    </div>
  );
}
